const waitForVideoEvent = (
  video: HTMLVideoElement,
  eventName: 'loadeddata' | 'seeked',
): Promise<void> =>
  new Promise((resolve, reject) => {
    const cleanup = () => {
      video.removeEventListener(eventName, handleEvent);
      video.removeEventListener('error', handleError);
    };
    const handleEvent = () => {
      cleanup();
      resolve();
    };
    const handleError = () => {
      cleanup();
      reject(new Error(`Video failed before "${eventName}"`));
    };
    video.addEventListener(eventName, handleEvent);
    video.addEventListener('error', handleError);
  });

/** JPEG data urls of `videoUrl` at each offset, `width` pixels wide. */
const captureVideoFrames = async ({
  videoUrl,
  offsetsSeconds,
  width,
}: {
  videoUrl: string;
  offsetsSeconds: number[];
  width: number;
}): Promise<string[]> => {
  const video = document.createElement('video');
  // Without CORS the canvas is tainted and `toDataURL` throws.
  video.crossOrigin = 'anonymous';
  video.muted = true;
  video.preload = 'auto';
  video.src = videoUrl;

  try {
    await waitForVideoEvent(video, 'loadeddata');

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = Math.round((width * video.videoHeight) / video.videoWidth);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D context is not available');

    const frames: string[] = [];
    for (const offsetSeconds of offsetsSeconds) {
      video.currentTime = offsetSeconds;
      await waitForVideoEvent(video, 'seeked');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      frames.push(canvas.toDataURL('image/jpeg', 0.85));
    }
    return frames;
  } finally {
    video.removeAttribute('src');
    video.load();
  }
};

export default captureVideoFrames;
